// Contracts for pot (jackpot): zod schemas for inputs, socket events, public state types.
import { z } from 'zod';
import type { ApiError } from '../errors';
import { zAmount, zIdempotencyKey } from '../common';

export const POT_PHASES = ['open', 'spinning', 'finished'] as const;
/** open = taking stakes (countdown runs once 2+ players are in), spinning = wheel animates to the result, finished = winner shown */
export type PotPhase = (typeof POT_PHASES)[number];

/**
 * POST /api/pot/bet — join the pot or top up the own stake (one position per player per round; every
 * top-up is a separate bet with its own idempotency key). Stakes close when the round starts.
 */
export const zPotBet = z
  .object({
    /** round the client sees; if it is no longer the open round → ROUND_ALREADY_STARTED */
    roundId: z.string().uuid().optional(),
    amount: zAmount,
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export type PotBetInput = z.input<typeof zPotBet>;

/** One position on the wheel (all stakes of one player in the round, summed). */
export interface PotPlayerView {
  /** User.publicId — the client compares it with `me.publicId` to mark "You" */
  playerId: number;
  nickname: string;
  avatar: string;
  /** total stake in the round, tokens */
  stake: number;
  /** position = order of the first stake; sector colour = palette[position % palette.length] (own sector: "you" colour) */
  position: number;
  /**
   * The house bot (docs/superpowers/specs/2026-10-01-house-bots-design.md): stakes the house's tokens when a lone player
   * waited `botDelayMs`; `playerId` = 0, `nickname` empty — the client labels it "bot".
   */
  bot?: true;
}

/** `PotPlayerView.playerId` of the house bot (User.publicId starts at 1). */
export const POT_BOT_PLAYER_ID = 0;

/** Server-computed result; the wheel only animates to it. */
export interface PotSpin {
  winnerPlayerId: number;
  /** winning ticket, 0 ≤ ticket < total; player i owns [Σstake_<i, Σstake_≤i) */
  ticket: number;
  /** wheel-local angle (deg, clockwise from the wheel's top) that stops under the pointer */
  stopAngle: number;
  total: number;
  fee: number;
  /** credited to the winner = total − fee */
  payout: number;
}

export interface PotTimings {
  countdownMs: number;
  spinMs: number;
  finishedMs: number;
}

/** GET /api/pot/state, ack of `pot:join`, and the `pot:state` push (every phase change). */
export interface PotState {
  phase: PotPhase;
  roundId: string;
  /** "#52118" */
  roundNo: number;
  serverSeedHash: string;
  /** revealed in phase `finished` */
  serverSeed: string | null;
  /** clientSeed of the shared round (`round:<roundId>`), nonce is always 0 */
  clientSeed: string;
  /** players in order of first stake (= wheel sectors, clockwise from the top) */
  players: PotPlayerView[];
  total: number;
  /** number of stakes accepted in the round; grows monotonically (clients drop stale `pot:players`) */
  seq: number;
  /** epoch ms when the round starts; null while fewer than `minPlayers` are in */
  countdownEndsAt: number | null;
  /** epoch ms of the current phase start / planned end (end is null in `open`: see countdownEndsAt) */
  phaseStartedAt: number;
  phaseEndsAt: number | null;
  /** server clock at send time (epoch ms) — client computes its clock offset */
  serverNow: number;
  timings: PotTimings;
  maxPlayers: number;
  minPlayers: number;
  /** house fee, basis points of the bank (1000 = 10%) */
  feeBps: number;
  minBet: number;
  maxBet: number;
  /** null while open */
  spin: PotSpin | null;
  /** epoch ms when the house bot takes a seat if nobody else joins the lone player; null = no bot due */
  botAt?: number | null;
  /** how long a lone player waits for a house bot, ms; null / absent = house bots are off */
  botDelayMs?: number | null;
}

/** `pot:players` — the stake list changed (join / top-up); also carries the countdown once it starts. */
export interface PotPlayersEvent {
  roundId: string;
  seq: number;
  players: PotPlayerView[];
  total: number;
  countdownEndsAt: number | null;
  serverNow: number;
}

/** POST /api/pot/bet response */
export interface PotBetResult {
  roundId: string;
  /** my position after this stake (stake = my total in the round) */
  player: PotPlayerView;
  balance: number;
  /** true: this idempotencyKey was already used, nothing was debited now */
  duplicate: boolean;
}

/** Public round result stored on GameRound.result (verifiable on the fair page with `verifyPot`). */
export interface PotRoundResult extends PotSpin {
  /** entries in wheel order: id = String(playerId); the house bot: id "bot", `bot: true` */
  entries: Array<{ id: string; stake: number; bot?: true }>;
  winnerIndex: number;
}

export interface PotServerToClient {
  /** full state on join and on every phase change (open / spinning with `spin` / finished with `serverSeed`) */
  'pot:state': (s: PotState) => void;
  /** players / shares / countdown while open */
  'pot:players': (e: PotPlayersEvent) => void;
}

export interface PotClientToServer {
  /** joins the pot room and returns the current state */
  'pot:join': (ack: (r: PotState | ApiError) => void) => void;
  'pot:leave': () => void;
}

/** Chance of a stake in the bank, 0..1. */
export function potShare(stake: number, total: number): number {
  return total > 0 ? stake / total : 0;
}
